/**
 * Workshop Scheduler Utility
 * Automatically calculates and updates workshop timing for Sunday and Wednesday
 */

// Workshop configuration - Updated for Sunday 10:00 AM and Wednesday 10:00 AM
const WORKSHOP_CONFIG = {
  daysOfWeek: [0, 3], // Sunday (0) and Wednesday (3)
  startTime: { hour: 10, minute: 0 }, // 10:00 AM
  endTime: { hour: 13, minute: 0 }, // 1:00 PM (13:00)
  timezone: 'Asia/Kolkata', // IST timezone
  duration: 3 // 3 hours
};

/**
 * Get the next workshop date from current date
 * @returns {Date} Next workshop date
 */
export function getNextWorkshopDate() {
  const now = new Date();
  const currentDay = now.getDay();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  // Calculate days until next workshop (Sunday or Wednesday)
  let daysUntilWorkshop = null;

  // Check if today is a workshop day
  if (WORKSHOP_CONFIG.daysOfWeek.includes(currentDay)) {
    const workshopTime = WORKSHOP_CONFIG.startTime.hour * 60 + WORKSHOP_CONFIG.startTime.minute;
    const currentTime = currentHour * 60 + currentMinute;

    if (currentTime < workshopTime) {
      // Workshop hasn't started yet today
      daysUntilWorkshop = 0;
    }
  }

  // If not today or workshop has passed, find next workshop day
  if (daysUntilWorkshop === null) {
    // Calculate days until each workshop day
    const daysToWorkshops = WORKSHOP_CONFIG.daysOfWeek.map(workshopDay => {
      let diff = workshopDay - currentDay;
      if (diff <= 0) diff += 7; // If day has passed this week, get next week
      return diff;
    });

    // Get the minimum (closest workshop day)
    daysUntilWorkshop = Math.min(...daysToWorkshops);
  }

  // Create next workshop date
  const nextWorkshop = new Date(now);
  nextWorkshop.setDate(now.getDate() + daysUntilWorkshop);
  nextWorkshop.setHours(WORKSHOP_CONFIG.startTime.hour);
  nextWorkshop.setMinutes(WORKSHOP_CONFIG.startTime.minute);
  nextWorkshop.setSeconds(0);
  nextWorkshop.setMilliseconds(0);

  return nextWorkshop;
}

/**
 * Get day suffix (st, nd, rd, th)
 * @param {number} day - Day of month
 * @returns {string} Suffix
 */
function getDaySuffix(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

/**
 * Get formatted workshop date string (WITHOUT day name)
 * Format: "December 22nd"
 * @returns {string} Formatted date string
 */
export function getFormattedWorkshopDate() {
  const nextWorkshop = getNextWorkshopDate();

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const monthName = monthNames[nextWorkshop.getMonth()];
  const day = nextWorkshop.getDate();
  const daySuffix = getDaySuffix(day);

  return `${monthName} ${day}${daySuffix}`;
}

/**
 * Get workshop time string
 * @returns {string} Workshop time string
 */
export function getWorkshopTimeString() {
  const startTime = `${WORKSHOP_CONFIG.startTime.hour === 12 ? 12 : WORKSHOP_CONFIG.startTime.hour > 12 ? WORKSHOP_CONFIG.startTime.hour - 12 : WORKSHOP_CONFIG.startTime.hour}:${WORKSHOP_CONFIG.startTime.minute.toString().padStart(2, '0')} ${WORKSHOP_CONFIG.startTime.hour >= 12 ? 'PM' : 'AM'}`;

  const endTime = `${WORKSHOP_CONFIG.endTime.hour === 12 ? 12 : WORKSHOP_CONFIG.endTime.hour > 12 ? WORKSHOP_CONFIG.endTime.hour - 12 : WORKSHOP_CONFIG.endTime.hour}:${WORKSHOP_CONFIG.endTime.minute.toString().padStart(2, '0')} ${WORKSHOP_CONFIG.endTime.hour >= 12 ? 'PM' : 'AM'}`;

  return `${startTime} - ${endTime} IST`;
}

/**
 * Get countdown target date (next workshop start time)
 * @returns {Date} Target date for countdown
 */
export function getCountdownTargetDate() {
  return getNextWorkshopDate();
}

/**
 * Check if it's time to update (Sunday or Wednesday at 12:00 AM)
 * @returns {boolean} True if it's update time
 */
export function isUpdateTime() {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  const minute = now.getMinutes();

  // Update at midnight on Sunday (0) or Wednesday (3)
  return WORKSHOP_CONFIG.daysOfWeek.includes(day) && hour === 0 && minute === 0;
}

/**
 * Get workshop status text
 * @returns {string} Status text
 */
export function getWorkshopStatus() {
  const now = new Date();
  const nextWorkshop = getNextWorkshopDate();
  const timeDiff = nextWorkshop.getTime() - now.getTime();
  const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

  if (daysLeft === 0) {
    return "Workshop is TODAY!";
  } else if (daysLeft === 1) {
    return "Workshop is TOMORROW!";
  } else if (daysLeft <= 7) {
    return `Workshop in ${daysLeft} days`;
  } else {
    return "Next workshop coming soon";
  }
}

/**
 * Get day name from day number
 * @param {number} dayNumber - Day number (0-6)
 * @returns {string} Day name
 */
function getDayName(dayNumber) {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return dayNames[dayNumber];
}

/**
 * Get workshop details object
 * @returns {Object} Workshop details
 */
export function getWorkshopDetails() {
  const nextWorkshop = getNextWorkshopDate();
  const nextWorkshopDay = nextWorkshop.getDay();

  return {
    date: nextWorkshop,
    formattedDate: getFormattedWorkshopDate(),
    timeString: getWorkshopTimeString(),
    status: getWorkshopStatus(),
    countdown: nextWorkshop.toISOString(),
    dayOfWeek: getDayName(nextWorkshopDay),
    duration: `${WORKSHOP_CONFIG.duration} hours`,
    timezone: 'IST',
    allWorkshopDays: WORKSHOP_CONFIG.daysOfWeek.map(day => getDayName(day)).join(' & ')
  };
}